import { inject, Injectable } from '@angular/core';
import { ref, Storage, uploadBytes } from '@angular/fire/storage';
import { Survey } from '../models/survey.model';
import { getAllFSFiles, readFSFile, logger } from '@damofire/data-commons';
import { countSurvey, SurveyLogContext, SurveyLogService } from './survey-log.service';

export interface SurveyDumpProgress {
  onStart(total: number): void;
  onPhotoComplete(completed: number, total: number): void;
}

@Injectable({ providedIn: 'root' })
export class SurveyDumpService {
  private readonly storage = inject(Storage);
  private readonly surveyLog = inject(SurveyLogService);

  async dumpSurvey(surveyId: string, survey: Survey, progress?: SurveyDumpProgress): Promise<void> {
    await this.surveyLog.track('survey.dump', { surveyId, source: 'firestore', after: countSurvey(survey) }, (tracked) => this.uploadDump(surveyId, survey, tracked, progress));
  }

  private async uploadDump(surveyId: string, survey: Survey, tracked: SurveyLogContext, progress?: SurveyDumpProgress): Promise<void> {
    const date = new Date().toISOString();
    const basePath = `survey-dumps/${surveyId}/${date}`;
    const imageFiles = await this.listImages(surveyId);
    progress?.onStart(imageFiles.length);

    // 1. Survey JSON.
    const jsonBlob = new Blob([JSON.stringify(survey, null, 2)], { type: 'application/json' });
    await this.upload(`${basePath}/survey.json`, jsonBlob, 'application/json');

    // 2. Every image under the survey's filesystem folder (photos are keyed under `${surveyId}/...`).
    let completed = 0;
    let failedImages = 0;
    tracked['imageCount'] = imageFiles.length;
    for (const file of imageFiles) {
      const relativePath = file.uri.replace(new RegExp(`(.+?)/${surveyId}/`), `${surveyId}/`);
      try {
        const fileContent = await this.readFile(relativePath);
        const decodedImage = this.base64ToArrayBuffer(fileContent);
        const blob = new Blob([decodedImage], { type: 'image/jpeg' });
        await this.upload(`${basePath}/images/${relativePath}`, blob, 'image/jpeg');
      } catch (e) {
        failedImages++;
        logger.error('Failed to dump survey image', { path: relativePath, category: 'survey', error: e });
      } finally {
        completed++;
        progress?.onPhotoComplete(completed, imageFiles.length);
      }
    }

    tracked['failedImages'] = failedImages;
  }

  private async upload(path: string, blob: Blob, contentType: string): Promise<void> {
    await uploadBytes(ref(this.storage, path), blob, { contentType });
  }

  private listImages(surveyId: string) {
    return getAllFSFiles(surveyId);
  }

  private readFile(path: string) {
    return readFSFile(path);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const commaIndex = base64.indexOf(',');
    const clean = commaIndex >= 0 ? base64.slice(commaIndex + 1) : base64;
    const binaryString = atob(clean);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
